/**
 * The rules and wording behind the player's Managers page, the Discover Managers widget and the
 * subscribe / cancel dialogs (#133), kept pure so they can be tested on their own.
 *
 * Statuses are the server's (Player/GetManagers): Available, Pending, Subscribed, Declined. Only an
 * Available manager can be selected or requested; the server enforces the same rule and skips any
 * manager that is no longer Available by the time a request arrives.
 */

export const MANAGER_STATUS = {
  AVAILABLE: "Available",
  PENDING: "Pending",
  SUBSCRIBED: "Subscribed",
  DECLINED: "Declined",
};

/** The Managers page tabs: key, label, the server's status filter, and the counter field. */
export const MANAGER_TABS = [
  { key: "all", label: "All Managers", status: "All", countField: "allCount" },
  { key: "subscribed", label: "My Managers", status: MANAGER_STATUS.SUBSCRIBED, countField: "subscribedCount" },
  { key: "pending", label: "Pending", status: MANAGER_STATUS.PENDING, countField: "pendingCount" },
];

/** Managers shown per page on the Managers page (the acceptance criteria assume 6). */
export const MANAGERS_PAGE_SIZE = 6;

/** Managers shown in the dashboard's Discover Managers widget. */
export const DISCOVER_MANAGERS_COUNT = 5;

export const CANCELLED_MESSAGE = "Subscription request cancelled.";

const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

/** Whether a manager can be ticked or requested. */
export const isSelectable = (manager) => manager?.status === MANAGER_STATUS.AVAILABLE;

/**
 * Whether a manager's row is drawn dimmed (#303): Declined only, the one status nothing more can be
 * done with. Pending and Subscribed rows cannot be ticked either, but they are live relationships -
 * a Pending one can be cancelled - so they keep normal text and their status badge tells the state.
 */
export const isDimmed = (manager) => manager?.status === MANAGER_STATUS.DECLINED;

/**
 * Why a manager's checkbox is disabled, for its tooltip; null when it is not.
 *
 * @param {string} status The manager's status.
 * @returns {string|null}
 */
export const lockReason = (status) => {
  switch (status) {
    case MANAGER_STATUS.PENDING:
      return "Request already sent";
    case MANAGER_STATUS.SUBSCRIBED:
      return "Already subscribed";
    case MANAGER_STATUS.DECLINED:
      return "Declined by manager. A new request cannot be sent";
    default:
      return null;
  }
};

/** "1 manager selected" / "N managers selected". */
export const selectedLabel = (count) => `${plural(count, "manager")} selected`;

/** "1 sport" / "N sports", the Discover Managers widget's count (#212). */
export const sportsLabel = (count) => plural(count, "sport");

/** The confirmation dialog's sentence above the list of managers. */
export const confirmIntro = (count) =>
  count === 1
    ? "Send a subscription request to the following manager:"
    : `Send subscription requests to the following ${count} managers:`;

/** The confirmation dialog's send button (#296): "Send Request" for one manager, "Send Requests" for more. */
export const confirmButtonLabel = (count) => (count === 1 ? "Send Request" : "Send Requests");

/**
 * The success message after Send Request(s), worded from what the server actually did.
 *
 * @param {{sentCount: number, skippedCount: number}} result The server's counts.
 * @param {string} [pendingPlace] Where the player finds the requests ("Pending tab" on the web).
 * @returns {string}
 */
export const sentMessage = ({ sentCount = 0, skippedCount = 0 } = {}, pendingPlace = "Pending tab") => {
  const sent =
    sentCount === 1
      ? `Subscription request sent to 1 manager. You can find it in the ${pendingPlace}.`
      : `Subscription requests sent to ${sentCount} managers. You can find them in the ${pendingPlace}.`;
  if (!skippedCount) return sent;
  const skipped =
    skippedCount === 1
      ? "1 manager was skipped because their status changed."
      : `${skippedCount} managers were skipped because their status changed.`;
  return `${sent} ${skipped}`;
};

/**
 * The header "select all" checkbox: ticked when every Available manager in the current results is
 * selected, indeterminate when only some are, disabled when there are none to select.
 *
 * @param {number} availableCount Available managers in the current results (all pages).
 * @param {number} selectedCount How many of them are selected.
 * @returns {{checked: boolean, indeterminate: boolean, disabled: boolean}}
 */
export const headerCheckboxState = (availableCount, selectedCount) => ({
  checked: availableCount > 0 && selectedCount >= availableCount,
  indeterminate: selectedCount > 0 && selectedCount < availableCount,
  disabled: availableCount === 0,
});

/**
 * Ticks or unticks one manager. Returns a new Map of id to manager, so a selection survives paging
 * and still knows each selected manager's name and tournament for the confirmation dialog.
 *
 * @param {Map<number, object>} selection The current selection.
 * @param {object} manager A row from Player/GetManagers.
 * @returns {Map<number, object>}
 */
export const toggleSelection = (selection, manager) => {
  const next = new Map(selection);
  if (next.has(manager.managerId)) next.delete(manager.managerId);
  else if (isSelectable(manager)) next.set(manager.managerId, manager);
  return next;
};

/** A Map selection holding exactly the selectable managers given. */
export const selectionOf = (managers) =>
  new Map((managers || []).filter(isSelectable).map((m) => [m.managerId, m]));

/** "Mike Rodriguez" -> "MR"; at most two letters. */
export const initials = (name) =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join("");

/** "Showing 1–6 of 12 managers"; nothing to show for an empty list. */
export const showingLabel = (page, pageSize, total) => {
  if (!total) return "";
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  return `Showing ${from}–${to} of ${plural(total, "manager")}`;
};
